'use client';

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from './button';

export function BackgroundPaths({
  title = 'Background Paths',
  logoSrc,
  logoAlt = 'Valinor Dynamics logo',
  videoSrc,
}: {
  title?: string;
  logoSrc?: string;
  logoAlt?: string;
  videoSrc?: string;
}) {
  const words = title.split(' ');

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-neutral-950">
      <div className="absolute inset-0">
        {videoSrc ? (
          <video
            aria-hidden="true"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="h-full w-full scale-[1.14] object-cover brightness-90 contrast-110 saturate-110"
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
        ) : null}
        <div className="absolute inset-0 bg-black/50" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.12),transparent_45%)]" />
      </div>

      <div className="relative z-10 container mx-auto px-4 text-center md:px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
          className="mx-auto max-w-4xl"
        >
          {logoSrc ? (
            <motion.img
              src={logoSrc}
              alt={logoAlt}
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              className="mx-auto mb-6 h-28 w-auto sm:h-32 md:h-36"
            />
          ) : null}

          <h1
            aria-label={title}
            className="mx-auto mb-8 max-w-4xl text-4xl font-bold leading-[0.96] tracking-tight text-neutral-100 [text-shadow:0_0_24px_rgba(255,255,255,0.22)] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
          >
            {words.map((word, wordIndex) => (
              <span
                key={wordIndex}
                aria-hidden="true"
                className="mr-3 inline-block last:mr-0 sm:mr-4"
              >
                {word.split('').map((letter, letterIndex) => (
                  <motion.span
                    key={`${wordIndex}-${letterIndex}`}
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      delay: wordIndex * 0.1 + letterIndex * 0.03,
                      type: 'spring',
                      stiffness: 150,
                      damping: 25,
                    }}
                    className="inline-block bg-gradient-to-br from-white via-neutral-200 to-neutral-500 bg-clip-text text-transparent"
                  >
                    {letter}
                  </motion.span>
                ))}
              </span>
            ))}
          </h1>

          <div className="group relative inline-block overflow-hidden rounded-2xl bg-gradient-to-b from-white/20 to-white/5 p-px backdrop-blur-lg shadow-lg transition-shadow duration-300 hover:shadow-xl">
            <Button
              asChild
              variant="ghost"
              className="rounded-[1.15rem] border border-white/20 bg-black/80 px-8 py-6 text-lg font-semibold text-white transition-all duration-300 hover:bg-black/95 hover:shadow-md group-hover:-translate-y-0.5 dark:hover:shadow-neutral-800/50"
            >
              <Link to="/capabilities">
                <span className="opacity-90 transition-opacity group-hover:opacity-100">
                  Explore Capabilities
                </span>
                <span className="ml-3 opacity-70 transition-all duration-300 group-hover:translate-x-1.5 group-hover:opacity-100">
                  {'->'}
                </span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
